import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import KnowledgeBaseSidebar, {
  KnowledgeBaseSidebarItem
} from '../Shared/KnowledgeBaseSidebar';
import '../Shared/Training.css';
import '../Shared/OrganizationalTraining.css';
import {
  ArrowIcon,
  LessonsIcon,
  ProgressIcon,
  TestsIcon,
  TimeIcon
} from '../Shared/TrainingIcons';
import type { Course, QuizQuestion } from './types';
import { getLessonById, mapApiLessonToOrganizationalCourse } from '../../../api/knowledgeApi';

type QuizAnswerMap = Record<string, string | string[]>;
type QuizOutcome = 'success' | 'failure';

/** Парсит строку вида "2 из 3" → { required, total } */
function parsePassRequirement(passRequirement: string, totalQuestions: number): { required: number; total: number } {
  const match = passRequirement.match(/(\d+)\s*из\s*(\d+)/i);
  if (match) {
    const required = parseInt(match[1], 10);
    const total = parseInt(match[2], 10);
    if (Number.isFinite(required) && Number.isFinite(total) && total > 0) {
      return { required, total };
    }
  }
  return { required: Math.ceil(totalQuestions * 0.6), total: totalQuestions };
}

/** Подсчёт правильных ответов по данным квиза и ответам пользователя */
function countCorrectAnswers(
  questions: QuizQuestion[],
  answers: QuizAnswerMap
): number {
  let correct = 0;
  for (const question of questions) {
    const userAnswer = answers[question.id];
    if (question.type === 'text') {
      const filled = Boolean(userAnswer && String(userAnswer).trim().length > 0);
      if (filled) correct += 1;
      continue;
    }
    const options = question.options ?? [];
    const correctIds = new Set(options.filter((o) => o.isCorrect).map((o) => o.id));
    if (correctIds.size === 0) continue;
    if (question.type === 'single') {
      if (userAnswer === undefined || userAnswer === null) continue;
      if (correctIds.has(String(userAnswer))) correct += 1;
    } else {
      const selected = Array.isArray(userAnswer) ? userAnswer : [];
      const selectedSet = new Set(selected.map(String));
      if (correctIds.size === selectedSet.size && Array.from(correctIds).every((id) => selectedSet.has(id))) {
        correct += 1;
      }
    }
  }
  return correct;
}

const OrganizationalTrainingTest: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId?: string }>();

  const [course, setCourse] = useState<Course | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Загружаем курс из API по ID
  useEffect(() => {
    const loadCourse = async () => {
      if (!courseId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Загружаем из API
        const apiLesson = await getLessonById(courseId);
        const mappedCourse = mapApiLessonToOrganizationalCourse(apiLesson, 'all');
        setCourse(mappedCourse);
      } catch (err) {
        console.error('Ошибка при загрузке курса:', err);
        setError('Не удалось загрузить курс. Попробуйте обновить страницу.');
      } finally {
        setIsLoading(false);
      }
    };

    loadCourse();
  }, [courseId]);

  const quiz = course?.quiz;

  const [quizQuestionIndex, setQuizQuestionIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState<QuizAnswerMap>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [reviewOutcome, setReviewOutcome] = useState<QuizOutcome | null>(null);
  const [reviewStats, setReviewStats] = useState<{ correct: number; total: number } | null>(null);
  const [showReviewScreen, setShowReviewScreen] = useState(false);

  useEffect(() => {
    if (!isLoading && (!course || !quiz)) {
      navigate('/knowledge-base/organizational-training');
    }
  }, [course, quiz, navigate, isLoading]);

  useEffect(() => {
    setQuizQuestionIndex(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setShowExitModal(false);
    setPendingNavigation(null);
    setReviewOutcome(null);
    setReviewStats(null);
    setShowReviewScreen(false);
  }, [courseId]);

  const hasQuizAnswers = useMemo(
    () =>
      Object.values(quizAnswers).some((value) =>
        Array.isArray(value) ? value.length > 0 : Boolean(value && String(value).trim().length > 0)
      ),
    [quizAnswers]
  );

  const recommendedCourse: Course | null = useMemo(() => {
    // Рекомендации будут загружаться из API в будущем
    return null;
  }, [course]);

  if (!course || !quiz) {
    return null;
  }

  const organizationalSidebarItems: KnowledgeBaseSidebarItem[] = [
    {
      id: 'all',
      label: 'Все разделы',
      onClick: () => navigate('/knowledge-base/organizational-training'),
      isActive: false
    }
  ];

  const profileSidebarItems: KnowledgeBaseSidebarItem[] = [];

  const currentQuestion: QuizQuestion | undefined = quiz.questions[quizQuestionIndex];
  const totalQuestions = quiz.questions.length;
  const answeredCount = quiz.questions.filter((question) => {
    const value = quizAnswers[question.id];
    if (question.type === 'text') {
      return Boolean(value && String(value).trim().length > 0);
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return Boolean(value);
  }).length;

  const handleChangeAnswer = (question: QuizQuestion, value: string) => {
    setQuizAnswers((prev) => {
      if (question.type === 'multiple') {
        const current = Array.isArray(prev[question.id])
          ? (prev[question.id] as string[])
          : [];
        const next = current.includes(value)
          ? current.filter((item) => item !== value)
          : [...current, value];
        return { ...prev, [question.id]: next };
      }
      return { ...prev, [question.id]: value };
    });
  };

  const handleTextAnswer = (questionId: string, value: string) => {
    setQuizAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmitQuiz = () => {
    if (answeredCount < totalQuestions || quizSubmitted) {
      return;
    }
    setQuizSubmitted(true);
    const correct = countCorrectAnswers(quiz.questions, quizAnswers);
    const { required, total } = parsePassRequirement(quiz.passRequirement, totalQuestions);
    const passed = correct >= required;
    setReviewOutcome(passed ? 'success' : 'failure');
    setReviewStats({ correct, total });
    setShowReviewScreen(true);
  };

  const isSubmitDisabled = answeredCount < totalQuestions || quizSubmitted;
  const shouldBlockNavigation = hasQuizAnswers && !quizSubmitted;

  const requestNavigation = (path: string) => {
    if (shouldBlockNavigation) {
      setPendingNavigation(path);
      setShowExitModal(true);
      return;
    }
    navigate(path);
  };

  const handleRetakeQuiz = () => {
    setQuizQuestionIndex(0);
    setQuizAnswers({});
    setQuizSubmitted(false);
    setShowExitModal(false);
    setReviewOutcome(null);
    setReviewStats(null);
    setShowReviewScreen(false);
  };

  const handleConfirmExit = () => {
    setShowExitModal(false);
    const target = pendingNavigation;
    setPendingNavigation(null);
    if (target) {
      setQuizAnswers({});
      setQuizSubmitted(false);
      navigate(target);
    }
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
    setPendingNavigation(null);
  };

  const renderRecommendationCard = () => {
    if (!recommendedCourse) {
      return null;
    }
    const course: Course = recommendedCourse;
    return (
      <div className="profile-training__quiz-recommendation-card">
        <div className="profile-training__quiz-recommendation-header">
          <span className="profile-training__quiz-recommendation-provider">
            {course.badge}
          </span>
          <span className="profile-training__quiz-recommendation-status">
            {course.completionPercent}% завершено
          </span>
        </div>
        <button
          type="button"
          className="profile-training__quiz-recommendation-title"
          onClick={() => {
            if (course.lessons[0]) {
              navigate(
                `/knowledge-base/organizational-training/${course.id}/lesson/${course.lessons[0].id}`
              );
            } else {
              navigate('/knowledge-base/organizational-training');
            }
          }}
        >
          {course.title}
        </button>
        <div className="profile-training__quiz-recommendation-meta">
          <span>{course.lessonsCount} уроков</span>
          <span>{course.testsCount} тест</span>
          <span>{course.duration}</span>
        </div>
      </div>
    );
  };

  const renderExitModal = () => (
    <div className="profile-training__modal-overlay">
      <div className="profile-training__modal profile-training__modal--compact">
        <div className="profile-training__modal-icon profile-training__modal-icon--warning" />
        <h3 className="profile-training__modal-title">Вы не завершили тест.</h3>
        <p className="profile-training__modal-description">
          При выходе ответы могут быть потеряны.
        </p>
        <div className="profile-training__modal-actions">
          <button
            type="button"
            className="profile-training__modal-secondary"
            onClick={handleCancelExit}
          >
            Отмена
          </button>
          <button
            type="button"
            className="profile-training__modal-primary"
            onClick={handleConfirmExit}
          >
            Выйти без сохранения
          </button>
        </div>
      </div>
    </div>
  );

  const renderResultScreen = () => {
    if (!showReviewScreen || !reviewOutcome || !reviewStats) {
      return null;
    }
    const isSuccess = reviewOutcome === 'success';
    const { correct, total } = reviewStats;
    const percent = total > 0 ? Math.round((correct / total) * 100) : 0;
    const stats = { percent, text: `Правильных ответов: ${correct} из ${total}` };

    return (
      <div className="profile-training__quiz-result">
        <div
          className={`profile-training__quiz-result-icon profile-training__quiz-result-icon--${
            isSuccess ? 'success' : 'failure'
          }`}
        />
        <h1 className="profile-training__quiz-result-title">
          {isSuccess ? 'Поздравляем! Вы успешно завершили курс:' : 'Тест не пройден'}
        </h1>
        <p
          className={`profile-training__quiz-result-percentage${
            isSuccess ? '' : ' profile-training__quiz-result-percentage--failure'
          }`}
        >
          {stats.percent}%
        </p>
        <p
          className={`profile-training__quiz-result-description${
            isSuccess ? '' : ' profile-training__quiz-result-description--failure'
          }`}
        >
          {stats.text}
        </p>

        {isSuccess ? (
          <>
            <div className="profile-training__quiz-result-recommendation-title">
              Следующий рекомендуемый курс:
            </div>
            {renderRecommendationCard()}
            <button
              type="button"
              className="profile-training__quiz-result-primary"
              onClick={() => requestNavigation('/knowledge-base/organizational-training')}
            >
              Перейти к следующему курсу
            </button>
          </>
        ) : (
          <div className="profile-training__quiz-result-actions">
            <button
              type="button"
              className="profile-training__quiz-result-primary"
              onClick={() => requestNavigation('/knowledge-base/organizational-training')}
            >
              Вернуться к курсу
            </button>
            <button
              type="button"
              className="profile-training__quiz-result-secondary"
              onClick={handleRetakeQuiz}
            >
              Пройти тест заново
            </button>
          </div>
        )}
      </div>
    );
  };

  // Обработка состояния загрузки
  if (isLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div>Загрузка теста...</div>
      </div>
    );
  }

  // Обработка ошибки загрузки
  if (error && !course) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
        <h3>Ошибка загрузки теста:</h3>
        <p>{error}</p>
        <button
          onClick={() => navigate('/knowledge-base/organizational-training')}
          style={{
            marginTop: '10px',
            padding: '8px 16px',
            background: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Вернуться к списку курсов
        </button>
      </div>
    );
  }

  if (!course || !quiz) {
    return null;
  }

  return (
    <div className="profile-training organizational-training">
      <div className="profile-training__container organizational-training__container">
        <KnowledgeBaseSidebar
          activeSection="organizational"
          organizationalItems={organizationalSidebarItems}
          profileItems={profileSidebarItems}
        />

        <main
          className={`profile-training__main${
            showReviewScreen ? ' profile-training__main--quiz-result' : ' profile-training__main--lesson'
          }`}
        >
          {showReviewScreen ? (
            renderResultScreen()
          ) : (
            <>
              <div className="profile-training__breadcrumbs">
                <button
                  type="button"
                  className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
                  onClick={() => requestNavigation('/knowledge-base')}
                >
                  Главная
                </button>
                <span className="profile-training__breadcrumb-separator">›</span>
                <button
                  type="button"
                  className="profile-training__breadcrumb-item profile-training__breadcrumb-link"
                  onClick={() => requestNavigation('/knowledge-base/organizational-training')}
                >
                  Организационное обучение
                </button>
                <span className="profile-training__breadcrumb-separator">›</span>
                <span className="profile-training__breadcrumb-item">{course.title}</span>
                <span className="profile-training__breadcrumb-separator">›</span>
                <span className="profile-training__breadcrumb-item">Тестирование</span>
              </div>

              <div className="profile-training__lesson-header">
                <div className="profile-training__lesson-back">
                  <button
                    className="profile-training__back-button"
                    onClick={() =>
                      requestNavigation(
                        `/knowledge-base/organizational-training/${course.id}/lesson/${course.lessons[course.lessons.length - 1]?.id ?? ''}`
                      )
                    }
                  >
                    <span className="profile-training__back-arrow">
                      <ArrowIcon />
                    </span>
                  </button>
                  <div className="profile-training__lesson-breadcrumb">
                    <span className="profile-training__lesson-course">{course.title}</span>
                    <span className="profile-training__lesson-status">100% завершено</span>
                  </div>
                </div>
                <div className="profile-training__lesson-metrics">
                  <div className="profile-training__lesson-metric">
                    <LessonsIcon />
                    <span>{course.lessonsCount} уроков</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <TestsIcon />
                    <span>{course.testsCount} тест</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <TimeIcon />
                    <span>{course.duration}</span>
                  </div>
                  <div className="profile-training__lesson-metric">
                    <ProgressIcon />
                    <span>100% завершено</span>
                  </div>
                </div>
              </div>

              <section className="profile-training__lesson-content">
                <header className="profile-training__lesson-content-header">
                  <div>
                    <h1 className="profile-training__lesson-title">{course.title}</h1>
                    <p className="profile-training__lesson-description">
                      Остался только тест — пройдите его, чтобы завершить курс.
                    </p>
                  </div>
                  <div className="profile-training__lesson-meta">
                    <TimeIcon />
                    <span>{quiz.duration}</span>
                    <span className="profile-training__lesson-dot">•</span>
                    <span>{quiz.questionsCount} вопросов</span>
                  </div>
                </header>

                <div className="profile-training__lesson-quiz">
                  <div className="profile-training__lesson-quiz-summary">
                    <div className="profile-training__lesson-quiz-summary-item">
                      <span className="profile-training__lesson-quiz-summary-icon">⏱</span>
                      <div>
                        <p className="profile-training__lesson-quiz-summary-label">Время выполнения</p>
                        <p className="profile-training__lesson-quiz-summary-value">{quiz.duration}</p>
                      </div>
                    </div>
                    <div className="profile-training__lesson-quiz-summary-item">
                      <span className="profile-training__lesson-quiz-summary-icon">❓</span>
                      <div>
                        <p className="profile-training__lesson-quiz-summary-label">Вопросов</p>
                        <p className="profile-training__lesson-quiz-summary-value">{quiz.questionsCount}</p>
                      </div>
                    </div>
                    <div className="profile-training__lesson-quiz-summary-item">
                      <span className="profile-training__lesson-quiz-summary-icon">🎯</span>
                      <div>
                        <p className="profile-training__lesson-quiz-summary-label">Условие прохождения</p>
                        <p className="profile-training__lesson-quiz-summary-value">{quiz.passRequirement}</p>
                      </div>
                    </div>
                  </div>

                  <div className="profile-training__lesson-quiz-progress">
                    <div
                      className="profile-training__lesson-quiz-progress-bar"
                      style={{ width: `${(answeredCount / totalQuestions) * 100}%` }}
                    />
                  </div>

                  {currentQuestion && (
                    <div className="profile-training__lesson-quiz-question">
                      <h3>
                        {currentQuestion.order}. {currentQuestion.text}
                      </h3>
                      {currentQuestion.type === 'text' ? (
                        <textarea
                          className="profile-training__lesson-quiz-textarea"
                          placeholder={currentQuestion.placeholder ?? ''}
                          value={(quizAnswers[currentQuestion.id] as string) ?? ''}
                          onChange={(event) => handleTextAnswer(currentQuestion.id, event.target.value)}
                        />
                      ) : (
                        <div className="profile-training__lesson-quiz-options">
                          {currentQuestion.options?.map((option) => {
                            const isMultiple = currentQuestion.type === 'multiple';
                            const answer = quizAnswers[currentQuestion.id];
                            const isChecked = isMultiple
                              ? Array.isArray(answer) && answer.includes(option.id)
                              : answer === option.id;
                            return (
                              <label key={option.id} className="profile-training__lesson-quiz-option">
                                <input
                                  type={isMultiple ? 'checkbox' : 'radio'}
                                  name={currentQuestion.id}
                                  value={option.id}
                                  checked={isChecked}
                                  onChange={() => handleChangeAnswer(currentQuestion, option.id)}
                                />
                                <span>{option.label}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="profile-training__lesson-quiz-pagination">
                    {quiz.questions.map((question, index) => {
                      const answer = quizAnswers[question.id];
                      const isAnswered =
                        question.type === 'text'
                          ? Boolean(answer && String(answer).trim().length > 0)
                          : Array.isArray(answer)
                          ? answer.length > 0
                          : Boolean(answer);
                      return (
                        <button
                          key={question.id}
                          type="button"
                          className={`profile-training__lesson-quiz-page${
                            index === quizQuestionIndex ? ' profile-training__lesson-quiz-page--active' : ''
                          }${isAnswered ? ' profile-training__lesson-quiz-page--answered' : ''}`}
                          onClick={() => setQuizQuestionIndex(index)}
                        >
                          {question.order}
                        </button>
                      );
                    })}
                  </div>

                  <div className="profile-training__lesson-quiz-warning">
                    <strong>Обратите внимание:</strong> после отправки теста редактирование ответов станет
                    недоступным.
                  </div>

                  <button
                    type="button"
                    className="profile-training__lesson-quiz-submit"
                    onClick={handleSubmitQuiz}
                    disabled={isSubmitDisabled}
                  >
                    Отправить на проверку
                  </button>
                </div>
              </section>
            </>
          )}
        </main>

        <aside className="profile-training__recent profile-training__lesson-sidebar">
          <div className="profile-training__lesson-sidebar-card">
            <div className="profile-training__lesson-sidebar-header">
              <h3>{course.title}</h3>
              <span className="profile-training__course-badge profile-training__course-badge--completed">
                Завершено
              </span>
            </div>
            <p className="organizational-training__test-note">
              Прогресс по урокам 100%. Осталось дождаться проверки теста.
            </p>
          </div>
        </aside>
      </div>

      {showExitModal && renderExitModal()}
    </div>
  );
};

export default OrganizationalTrainingTest;

